import { formatByMask } from "dash-common";
import { ModalContext, toast } from "dash-ui";
import {
  GetBrokerByIdQuery,
  GetPortfoliosByPublicFolderQuery,
  useSendMessageToAgentMutation,
} from "gql/generated.graphql";
import { useContext } from "react";
import { useForm } from "react-hook-form";
import { FiX } from "react-icons/fi";
import { MdPhone } from "react-icons/md";

type AgentContactModalProps = {
  // I could not type just the broker and portfolio data, so I receive the entire queries in the component
  getBrokerQuery?: GetBrokerByIdQuery;
  getPortfolioQuery?: GetPortfoliosByPublicFolderQuery;
  propertyId?: string;
};
// Form data type
type ContactFormData = {
  full_name: string;
  email: string;
  phone?: string;
  message: string;
};

export function AgentContactModal(props: AgentContactModalProps) {
  // Abstract broker data from query data
  const broker = props.getBrokerQuery?.getBrokerById;
  // Abstract portfolio data from query data
  const portfolio =
    props.getPortfolioQuery?.getPortfoliosByPublicFolder.collection?.find(
      function (data) {
        return data.property?._id === props.propertyId;
      }
    );
  // Hooks
  const contactForm = useForm<ContactFormData>();
  const useModal = useContext(ModalContext);
  // Queries and mutations
  const [sendMessageToAgent] = useSendMessageToAgentMutation();
  // Handle close modal
  function closeModal() {
    useModal.toggleModal(false, "BrokerInfoModal");
  }
  // Handle send message
  async function sendMessage(formData: ContactFormData) {
    if (!broker || !portfolio) return;
    await sendMessageToAgent({
      variables: {
        input: {
          broker_email: broker.account.email,
          broker_name: broker.profile.firstName,
          customer_email: formData.email,
          customer_message: formData.message,
          customer_name: formData.full_name,
          customer_phone: formData.phone || "",
          property_name: portfolio.property?.name || "",
          portfolio_folder_name: portfolio.portfolioFolder?.name || "",
        },
      },
    });
    contactForm.reset();
    toast.success(
      `Your message has been sent and the agent will contact you shortly.`
    );
  }
  // Return nothing if modal is closed
  if (!useModal.isOpenCurrent["BrokerInfoModal"] || !broker || !portfolio)
    return <></>;
  // Return HTML component
  return (
    <div className="fixed top-0 left-0 bg-black bg-opacity-30 w-full max-w-screen h-screen overflow-auto flex flex-col items-center z-10 px-4 py-20">
      <div className="flex flex-col bg-white rounded w-full max-w-screen-lg p-[5%]">
        <header className="relative">
          <img
            src={broker.profile.avatarFile?.publicUrl}
            alt={broker.profile.firstName}
            className="w-full object-cover rounded-lg"
          />
          <button className="absolute right-4 top-4 text-white bg-sky-600 rounded flex h-8 w-8 items-center justify-center">
            <FiX className="h-6 w-6" onClick={closeModal} />
          </button>
        </header>
        <div className="flex flex-col pb-10">
          <div className="mt-4">
            <h1 className="text-xl text-slate-800 font-bold font-[montserrat]">
              {`${broker.profile.firstName} ${broker.profile.lastName}`}
            </h1>
            {broker.agencyName && (
              <h2 className="font-semibold">{broker.agencyName}</h2>
            )}
          </div>
          <p className="text-slate-600 mt-2">{broker.description}</p>
          <p className="text-slate-600 font-bold uppercase mt-4">Contact Me</p>
          {broker.account.phone && (
            <div className="flex flex-col gap-2 mt-4">
              <a
                href={`tel:${broker.account.phone?.dialCode}${broker.account.phone?.number}`}
                className="flex items-center justify-center rounded text-white bg-sky-600 h-10">
                <MdPhone className="h-4 w-4" />{" "}
                {`${broker.account.phone?.dialCode} ${formatByMask(
                  broker.account.phone?.number,
                  "(###) ###-####"
                )}`}
              </a>
            </div>
          )}
          <form
            id="agent-contact-modal-form"
            className="agent-contact-modal-form flex flex-col gap-2 mt-6 text-slate-600"
            onSubmit={contactForm.handleSubmit(sendMessage)}>
            <label className="text-slate-800 font-semibold text-sm">
              Your name*
              <input
                className="w-full border border-slate-300 rounded h-10 px-4 text-slate-800 text-sm font-normal focus:outline-none"
                {...contactForm.register("full_name")}
                required
              />
            </label>
            <label className="text-slate-800 font-semibold text-sm">
              Email*
              <input
                className="w-full border border-slate-300 rounded h-10 px-4 text-slate-800 text-sm font-normal focus:outline-none"
                {...contactForm.register("email")}
                required
                type="email"
              />
            </label>
            <label className="text-slate-800 font-semibold text-sm">
              Phone
              <input
                className="w-full border border-slate-300 rounded h-10 px-4 text-slate-800 text-sm font-normal focus:outline-none"
                {...contactForm.register("phone")}
              />
            </label>
            <label className="text-slate-800 font-semibold text-sm">
              Message*
              <textarea
                className="w-full border border-slate-300 rounded h-28 px-4 text-slate-800 py-2 text-sm font-normal focus:outline-none"
                required
                {...contactForm.register("message")}
              />
            </label>
            <button
              className="h-10 px-4 text-white bg-sky-600 rounded"
              type="submit">
              Send Message
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
